import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useProfessionalMerchant } from "@/hooks/useProfessionalMerchant";
import { useSubscription } from "@/hooks/useSubscription";
import { useTeamMemberProfile } from "@/hooks/useTeamMemberProfile";
import { useNavigationBlocker, setBypassBlocker } from "@/hooks/useNavigationBlocker";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { 
  Loader2,
  ExternalLink,
  Store,
  LogOut,
  Receipt,
  Settings,
  Menu,
  Briefcase,
  Activity,
  Plus,
  CreditCard,
  Clock
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { formatCurrency } from "@/lib/currency";
import { toast } from "sonner";
// Removed merchant-specific imports for professional-only version
import { SubscriptionPayment } from "@/components/SubscriptionPayment";
import { PaymentHistory } from "@/components/PaymentHistory";
import { FAQChatBot } from "@/components/FAQChatBot";
import { ProfessionalOverview } from "@/components/ProfessionalOverview";
import { ClientList } from "@/components/professional/ClientList";
import { ServicesList } from "@/components/professional/ServicesList";
import { CreateCollectionDialog } from "@/components/professional/CreateCollectionDialog";
import { NotificationSettings } from "@/components/NotificationSettings";
import { ExitConfirmationDialog } from "@/components/ExitConfirmationDialog";
import { CustomerFooter } from "@/components/CustomerFooter";
import { ContactSupport } from "@/components/ContactSupport";
import { TeamMemberSetup } from "@/components/TeamMemberSetup";
import { ProfessionalSidebar, type ProfessionalTerminology, type PaymentsSubView, type ServicesSubView, type ReportsSubView } from "@/components/ProfessionalSidebar";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

interface Participant {
  id: string;
  name: string;
  email: string;
  mobile: string;
  has_paid: boolean;
  midpoint_reminder_sent: boolean;
  final_reminder_sent: boolean;
}

interface SplitPaymentRequest {
  id: string;
  product_name: string;
  product_image_url: string | null;
  product_description: string | null;
  total_price: number;
  split_amount: number;
  deadline: string;
  deadline_type: string;
  customer_name: string;
  customer_email: string;
  status: string;
  created_at: string;
  completed_at: string | null;
  tax_rate: number;
  participants: Participant[];
}

interface ShakatahInvoice {
  id: string;
  invoice_number: string;
  period_start: string;
  period_end: string;
  total_fees: number;
  total_amount: number;
  status: string;
  sent_at: string | null;
  paid_at: string | null;
  created_at: string;
}

interface MerchantInfo {
  id: string;
  store_name: string;
  shopify_store_domain: string;
  notification_email: string | null;
  plan: string;
  business_address?: string | null;
  gst_number?: string | null;
  business_email?: string | null;
  business_phone?: string | null;
  contact_person_name?: string | null;
  currency?: string;
  platform_type?: string;
  onboarding_completed_at?: string | null;
  professional_terminology?: Record<string, string> | null;
}

function ProfessionalDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, signOut } = useAuth();
  const { role: merchantRole, isSuperAdmin, isManager, isViewOnly, canManageRefunds, canManageProducts, canAccessSettings, merchantId: roleMerchantId, loading: roleLoading } = useProfessionalMerchant();
  const { subscription, loading: subscriptionLoading } = useSubscription();
  const { needsSetup, needsTour, refetch: refetchProfile } = useTeamMemberProfile(roleMerchantId);
  const [requests, setRequests] = useState<SplitPaymentRequest[]>([]);
  const [shakatahInvoices, setShakatahInvoices] = useState<ShakatahInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [merchantInfo, setMerchantInfo] = useState<MerchantInfo | null>(null);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [showCreateCollectionDialog, setShowCreateCollectionDialog] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [noMerchantAssociation, setNoMerchantAssociation] = useState(false);

  const { 
    showDialog: showExitDialog, 
    confirmNavigation, 
    cancelNavigation 
  } = useNavigationBlocker({ when: true });

  // Navigation state from URL
  const activeTab = searchParams.get("tab") || "overview";
  const paymentsSubView = (searchParams.get("paymentsView") as PaymentsSubView) || "collections";
  const servicesSubView = (searchParams.get("servicesView") as ServicesSubView) || "services";
  const reportsSubView = (searchParams.get("reportsView") as ReportsSubView) || "analytics";
  const settingsSubView = (searchParams.get("settingsView") as "billing" | "preferences" | "notifications" | "info") || "billing";

  const setPaymentsSubView = useCallback((view: PaymentsSubView) => {
    setSearchParams(prev => { const p = new URLSearchParams(prev); p.set("paymentsView", view); return p; }, { replace: false });
  }, [setSearchParams]);

  const setServicesSubView = useCallback((view: ServicesSubView) => {
    setSearchParams(prev => { const p = new URLSearchParams(prev); p.set("servicesView", view); return p; }, { replace: false });
  }, [setSearchParams]);

  const setReportsSubView = useCallback((view: ReportsSubView) => {
    setSearchParams(prev => { const p = new URLSearchParams(prev); p.set("reportsView", view); return p; }, { replace: false });
  }, [setSearchParams]);

  const setActiveTab = useCallback((tab: string) => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      p.set("tab", tab);
      p.delete("paymentsView"); p.delete("servicesView"); p.delete("reportsView"); p.delete("settingsView");
      return p;
    }, { replace: false });
  }, [setSearchParams]);

  const setSettingsSubView = useCallback((view: "billing" | "preferences" | "notifications" | "info") => {
    setSearchParams(prev => { const p = new URLSearchParams(prev); p.set("settingsView", view); return p; }, { replace: false });
  }, [setSearchParams]);

  const handleNavigateToBilling = useCallback(() => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      p.set("tab", "settings"); p.set("settingsView", "billing");
      p.delete("paymentsView"); p.delete("servicesView"); p.delete("reportsView");
      return p;
    }, { replace: false });
  }, [setSearchParams]);

  useEffect(() => {
    if (!roleLoading && !isSuperAdmin && needsSetup) {
      setShowSetup(true);
    } else if (!roleLoading && !isSuperAdmin && needsTour) {
      setShowTour(true);
    }
  }, [roleLoading, isSuperAdmin, needsSetup, needsTour]);

  const fetchMerchantInfo = async () => {
    if (!user) return null;
    try {
      // Only look for professional merchant records
      const { data: merchantUsers, error } = await supabase
        .from('merchant_users')
        .select('merchant_id, role, merchants!inner(platform_type, shopify_store_domain)')
        .eq('user_id', user.id);

      if (error || !merchantUsers || merchantUsers.length === 0) {
        return null;
      }

      // Filter for professional only
      const profRecord = merchantUsers.find((mu: any) => {
        const m = mu.merchants;
        return m?.platform_type === 'standalone' && m?.shopify_store_domain?.includes('.professional.');
      });

      if (!profRecord) return null;

      const { data: merchant, error: merchantError } = await supabase
        .from('merchants')
        .select('*')
        .eq('id', profRecord.merchant_id)
        .single();

      if (merchantError || !merchant) return null;

      setMerchantInfo(merchant as unknown as MerchantInfo);
      return profRecord.merchant_id;
    } catch (error) {
      console.error("Error fetching professional merchant info:", error);
      return null;
    }
  };

  const fetchRequests = async (merchantId: string) => {
    try {
      const { data: requestsData, error: requestsError } = await supabase
        .from('split_payment_requests')
        .select('*')
        .eq('merchant_id', merchantId)
        .order('created_at', { ascending: false });

      if (requestsError) throw requestsError;

      if (!requestsData || requestsData.length === 0) {
        setRequests([]);
      } else {
        const requestIds = requestsData.map(r => r.id);
        const { data: participantsData } = await supabase
          .from('split_payment_participants')
          .select('*')
          .in('request_id', requestIds);

        setRequests(requestsData.map(request => ({
          ...request,
          participants: (participantsData || []).filter(p => p.request_id === request.id)
        })));
      }

      // Fetch Shakatah invoices
      const { data: invoicesData } = await supabase
        .from('shakatah_invoices')
        .select('*')
        .eq('merchant_id', merchantId)
        .order('created_at', { ascending: false });

      if (invoicesData) setShakatahInvoices(invoicesData);

    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      if (!user) { setLoading(false); return; }
      const merchantId = await fetchMerchantInfo();
      if (merchantId) {
        await fetchRequests(merchantId);
      } else {
        setNoMerchantAssociation(true);
        setLoading(false);
      }
    };
    loadData();
  }, [user]);

  // Redirect to onboarding if needed
  useEffect(() => {
    if (loading) return;
    if (noMerchantAssociation) {
      toast.info("Please complete your account setup to continue");
      setBypassBlocker(true);
      navigate('/professional/onboarding', { replace: true });
      setTimeout(() => setBypassBlocker(false), 100);
      return;
    }
    if (!merchantInfo) return;
    if (!merchantInfo.onboarding_completed_at) {
      toast.info("Please complete your setup to continue");
      setBypassBlocker(true);
      navigate('/professional/onboarding', { replace: true });
      setTimeout(() => setBypassBlocker(false), 100);
    }
  }, [loading, merchantInfo, noMerchantAssociation, navigate]);

  useEffect(() => {
    if (!roleLoading && !canAccessSettings && settingsSubView === 'billing') {
      setSearchParams(prev => {
        const p = new URLSearchParams(prev);
        p.set("settingsView", "notifications");
        return p;
      }, { replace: true });
    }
  }, [roleLoading, canAccessSettings]);

  const handleSignOut = async () => {
    setBypassBlocker(true);
    await signOut();
    navigate("/login", { replace: true });
    toast.success("Signed out successfully");
    setBypassBlocker(false);
  };

  const getStatusBadge = (status: string, deadline: string) => {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const isExpired = now > deadlineDate;
    const actualStatus = status === 'completed' ? 'completed' : (status === 'expired' || isExpired) ? 'expired' : 'pending';

    if (actualStatus === 'completed') return <Badge className="bg-green-500/10 text-green-600 border-green-500/20">Completed</Badge>;
    if (actualStatus === 'expired') return <Badge variant="destructive">Expired</Badge>;
    return <Badge className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20">Pending</Badge>;
  };

  const getPaidCount = (participants: Participant[]) => {
    const paid = participants.filter(p => p.has_paid).length;
    return { paid, total: participants.length };
  };

  const filteredRequests = requests;

  if (loading || roleLoading || subscriptionLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (showSetup && merchantInfo && user && merchantRole) {
    return (
      <TeamMemberSetup
        merchantId={merchantInfo.id}
        userId={user.id}
        role={merchantRole}
        storeName={merchantInfo.store_name}
        onComplete={() => {
          setShowSetup(false);
          refetchProfile();
          setShowTour(true);
        }}
      />
    );
  }

  const sidebarContent = (
    <ProfessionalSidebar
      activeTab={activeTab}
      paymentsSubView={paymentsSubView}
      servicesSubView={servicesSubView}
      reportsSubView={reportsSubView}
      settingsSubView={settingsSubView}
      onTabChange={(tab, subView) => {
        setSearchParams(prev => {
          const p = new URLSearchParams(prev);
          p.set("tab", tab);
          p.delete("paymentsView"); p.delete("servicesView"); p.delete("reportsView"); p.delete("settingsView");
          if (subView) {
            if (tab === "payments") p.set("paymentsView", subView);
            else if (tab === "services") p.set("servicesView", subView);
            else if (tab === "reports") p.set("reportsView", subView);
            else if (tab === "settings") p.set("settingsView", subView);
          }
          return p;
        }, { replace: false });
        setMobileSidebarOpen(false);
      }}
      onPaymentsSubViewChange={setPaymentsSubView}
      onServicesSubViewChange={setServicesSubView}
      onReportsSubViewChange={setReportsSubView}
      onSettingsSubViewChange={setSettingsSubView}
      pendingPaymentsCount={requests.filter(r => r.status === 'pending').length}
      pendingFeesCount={0}
      canAccessSettings={canAccessSettings}
      terminology={merchantInfo?.professional_terminology as ProfessionalTerminology || undefined}
    />
  );

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-20">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Sheet open={mobileSidebarOpen} onOpenChange={setMobileSidebarOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="lg:hidden">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="p-0 w-64">
                  {sidebarContent}
                </SheetContent>
              </Sheet>
              
              <div className="p-2 rounded-lg bg-professional/10 hidden sm:block">
                <Briefcase className="h-5 w-5 text-professional" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="font-semibold text-sm sm:text-base">{merchantInfo?.contact_person_name || merchantInfo?.store_name || 'Dashboard'}</h1>
                  <Badge 
                    variant="outline" 
                    className="text-xs hidden sm:inline-flex border-professional/50 text-professional bg-professional/10"
                  >
                    Professional
                  </Badge>
                  {merchantRole && (
                    <Badge variant="outline" className="capitalize text-xs hidden sm:inline-flex">
                      {merchantRole.replace('_', ' ')}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground hidden sm:block">{user?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-1 sm:gap-2">
              <ContactSupport 
                merchantName={merchantInfo?.store_name}
                merchantEmail={merchantInfo?.notification_email || undefined}
              />
              <Button variant="ghost" size="sm" onClick={handleSignOut} className="gap-1 sm:gap-2">
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Sign Out</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Layout */}
      <div className="flex flex-1 overflow-hidden">
        <div className="hidden lg:block h-[calc(100vh-57px)] sticky top-[57px]">
          {sidebarContent}
        </div>

        <main className="flex-1 overflow-y-auto pb-12">
          <div className="p-4 sm:p-6 max-w-5xl">
            {/* Trial info removed */}

            {/* Overview */}
            {activeTab === "overview" && (
              <ProfessionalOverview
                requests={requests}
                merchantInfo={merchantInfo}
                subscription={subscription}
                terminology={merchantInfo?.professional_terminology as ProfessionalTerminology || undefined}
                onNavigateToTab={(tab, subView) => {
                  setSearchParams(prev => {
                    const p = new URLSearchParams(prev);
                    p.set("tab", tab);
                    p.delete("paymentsView"); p.delete("servicesView"); p.delete("reportsView"); p.delete("settingsView");
                    if (subView) {
                      if (tab === "settings") p.set("settingsView", subView);
                      else if (tab === "payments") p.set("paymentsView", subView);
                      else if (tab === "services") p.set("servicesView", subView);
                      else if (tab === "reports") p.set("reportsView", subView);
                    }
                    return p;
                  }, { replace: false });
                }}
                onCreateCollection={() => setShowCreateCollectionDialog(true)}
              />
            )}

            {/* Payments Tab */}
            {activeTab === "payments" && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold">{merchantInfo?.professional_terminology?.orders || "Payments"}</h2>
                    <p className="text-sm text-muted-foreground">Manage your payment collections</p>
                  </div>
                  <Button className="bg-cyan-600 hover:bg-cyan-700" onClick={() => setShowCreateCollectionDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Collection
                  </Button>
                </div>
                {paymentsSubView === "collections" && (
                  <div className="space-y-4">
                    {requests.length === 0 ? (
                      <Card className="text-center py-12">
                        <CardContent>
                          <CreditCard className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                          <h3 className="font-medium mb-2">No collections yet</h3>
                          <p className="text-sm text-muted-foreground mb-4">Create your first payment collection to get started</p>
                          <Button className="bg-cyan-600 hover:bg-cyan-700" onClick={() => setShowCreateCollectionDialog(true)}>
                            Create Collection
                          </Button>
                        </CardContent>
                      </Card>
                    ) : (
                      filteredRequests.map((request) => {
                        const { paid, total } = getPaidCount(request.participants);
                        return (
                          <Card key={request.id} className="overflow-hidden">
                            <div className="p-4">
                              <div className="flex items-start justify-between mb-2">
                                <div>
                                  <h3 className="font-semibold">{request.product_name}</h3>
                                  <p className="text-sm text-muted-foreground">
                                    {paid}/{total} paid • {new Date(request.created_at).toLocaleDateString()}
                                  </p>
                                </div>
                                {getStatusBadge(request.status, request.deadline)}
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="font-medium">{formatCurrency(request.total_price, (merchantInfo?.currency as any) || 'INR')}</span>
                                <Button variant="ghost" size="sm" onClick={() => window.open(`/split/${request.id}`, '_blank')}>
                                  <ExternalLink className="h-4 w-4 mr-1" /> View
                                </Button>
                              </div>
                            </div>
                          </Card>
                        );
                      })
                    )}
                  </div>
                )}
                {paymentsSubView === "history" && (
                  <PaymentHistory merchantId={merchantInfo?.id || ''} />
                )}
              </div>
            )}

            {/* Services & Clients Tab */}
            {activeTab === "services" && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold">
                      {merchantInfo?.professional_terminology?.products || "Services"} & {merchantInfo?.professional_terminology?.customers || "Clients"}
                    </h2>
                    <p className="text-sm text-muted-foreground">Manage your service offerings and client relationships</p>
                  </div>
                </div>
                {servicesSubView === "services" && merchantInfo && (
                  <ServicesList merchantId={merchantInfo.id} currency={merchantInfo.currency || "INR"} terminology={merchantInfo.professional_terminology as ProfessionalTerminology || undefined} />
                )}
                {servicesSubView === "clients" && merchantInfo && (
                  <ClientList merchantId={merchantInfo.id} terminology={merchantInfo.professional_terminology as ProfessionalTerminology || undefined} />
                )}
              </div>
            )}

            {/* Reports Tab */}
            {activeTab === "reports" && (
              <div className="space-y-6">
                {reportsSubView === "analytics" && (
                  <Card className="text-center py-12">
                    <CardContent>
                      <Activity className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="font-medium mb-2">Analytics coming soon</h3>
                      <p className="text-sm text-muted-foreground">Track your collection performance</p>
                    </CardContent>
                  </Card>
                )}
                {reportsSubView === "activity" && (
                  <Card className="text-center py-12">
                    <CardContent>
                      <Activity className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="font-medium mb-2">Activity log coming soon</h3>
                      <p className="text-sm text-muted-foreground">Track all your recent actions and payments</p>
                    </CardContent>
                  </Card>
                )}
                {reportsSubView === "customers" && (
                  <Card className="text-center py-12">
                    <CardContent>
                      <h3 className="font-medium mb-2">Client insights coming soon</h3>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Settings Tab */}
            {activeTab === "settings" && (
              <div className="space-y-6">
                {settingsSubView === "billing" && canAccessSettings && (
                  <div className="space-y-6">
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <CreditCard className="h-5 w-5" />
                          Subscription & Billing
                        </CardTitle>
                        <CardDescription>
                          {subscription?.status === 'trialing' 
                            ? `You're on a free trial. ${subscription.daysRemaining ? `${subscription.daysRemaining} days remaining.` : ''}`
                            : subscription?.status === 'active'
                            ? `Current plan: ${subscription.plan.charAt(0).toUpperCase() + subscription.plan.slice(1)}`
                            : 'Choose a plan to continue'}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        {merchantInfo && subscription && (
                          <SubscriptionPayment
                            merchantId={merchantInfo.id}
                            currentPlan={subscription.plan}
                            currency={(merchantInfo?.currency as any) || 'INR'}
                            isProfessional={true}
                            onSuccess={async () => {
                              if (merchantInfo) {
                                await fetchRequests(merchantInfo.id);
                                window.location.reload();
                              }
                            }}
                          />
                        )}
                      </CardContent>
                    </Card>
                    {merchantInfo && <PaymentHistory merchantId={merchantInfo.id} />}

                    {/* Shakatah Invoices */}
                    {shakatahInvoices.length > 0 && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Receipt className="h-5 w-5" />
                            Shakatah Invoices
                          </CardTitle>
                          <CardDescription>Your billing history with Shakatah</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            {shakatahInvoices.map((invoice) => (
                              <div key={invoice.id} className="flex items-center justify-between p-4 rounded-lg border">
                                <div className="flex items-center gap-4">
                                  <div className={`p-2 rounded-lg ${invoice.status === 'paid' ? 'bg-green-500/10' : 'bg-blue-500/10'}`}>
                                    <Receipt className={`h-5 w-5 ${invoice.status === 'paid' ? 'text-green-600' : 'text-blue-600'}`} />
                                  </div>
                                  <div>
                                    <p className="font-mono font-semibold">{invoice.invoice_number}</p>
                                    <p className="text-sm text-muted-foreground">
                                      {new Date(invoice.period_start).toLocaleDateString()} - {new Date(invoice.period_end).toLocaleDateString()}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-4">
                                  <div className="text-right">
                                    <p className="font-semibold">{formatCurrency(invoice.total_amount)}</p>
                                    <p className="text-xs text-muted-foreground">{invoice.total_fees} fee(s)</p>
                                  </div>
                                  <Badge className={
                                    invoice.status === 'paid' ? 'bg-green-500/10 text-green-600' : 
                                    invoice.status === 'sent' ? 'bg-blue-500/10 text-blue-600' :
                                    'bg-yellow-500/10 text-yellow-600'
                                  }>
                                    {invoice.status === 'paid' ? 'Paid' : invoice.status === 'sent' ? 'Sent' : 'Draft'}
                                  </Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Financial statement removed */}
                  </div>
                )}

                {settingsSubView === "preferences" && canAccessSettings && merchantInfo && (
                  <Card className="text-center py-12">
                    <CardContent>
                      <h3 className="font-medium mb-2">Preferences coming soon</h3>
                    </CardContent>
                  </Card>
                )}

                {settingsSubView === "notifications" && merchantInfo && (
                  <NotificationSettings
                    merchantId={merchantInfo.id}
                    canEdit={canAccessSettings || !isViewOnly}
                    merchantEmail={merchantInfo.notification_email || user?.email}
                    isProfessional={true}
                    terminology={merchantInfo.professional_terminology as Record<string, string> | undefined}
                  />
                )}

                {settingsSubView === "info" && canAccessSettings && merchantInfo && (
                  <Card className="text-center py-12">
                    <CardContent>
                      <h3 className="font-medium mb-2">Business info settings coming soon</h3>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>
        </main>
      </div>
      
      <FAQChatBot context="merchant" />
      
      <ExitConfirmationDialog
        open={showExitDialog}
        onConfirm={confirmNavigation}
        onCancel={cancelNavigation}
      />
      
      {merchantInfo && (
        <CreateCollectionDialog
          open={showCreateCollectionDialog}
          onOpenChange={setShowCreateCollectionDialog}
          merchantId={merchantInfo.id}
          currency={(merchantInfo.currency || 'INR') as any}
          terminology={merchantInfo.professional_terminology as ProfessionalTerminology || undefined}
          onSuccess={() => fetchRequests(merchantInfo.id)}
        />
      )}
      
      <CustomerFooter tagline="Simplifying professional payments" variant="professional" />
    </div>
  );
}

export default ProfessionalDashboard;
